import { cache } from "react";
import tmpl from "templite";
import { merge } from "./locale-merge";
import { Localization } from "./localization-auto-generated";
import { structuredClone } from "./structured-clone";
import {
    InterpString,
    LocalizationBase,
    LocalizationLeaf,
    NoIterpString,
} from "./types";

// This type is not going to be needed elsewhere.  We only need it for internal use to traverse the localization tree.
type LocalizationSubGroup = {
    [k: string]: LocalizationBase;
};

export type I18nHive<L extends keyof Localization> = Pick<Localization, L>;

// This Type needs to exist because the `label` is not yet annotated with `l`.
type LocalizationValueLabelArrayInternal = Array<{
    label: LocalizationLeaf;
    value: string;
}> &
    LocalizationBase;

type LocalizationSubGroupArray = Array<LocalizationBase> & LocalizationBase;

const rec = <T extends Record<string, any>>(
    r: T | string | number | T[],
):
    | LocalizationSubGroupArray
    | LocalizationSubGroup
    | LocalizationLeaf
    | LocalizationValueLabelArrayInternal => {
    if (typeof r === "string") {
        return {
            __value: r,
        };
    } else if (typeof r === "number") {
        return {
            __value: r.toString(),
        };
    } else if (typeof r === "object" && Array.isArray(r)) {
        // We need to support two sides here.  The `LocalizationValueLabelArrayInternal` and `Array<LocalizationSubGroup>`
        const keys: Set<string> = new Set(
            r
                .flat()
                .map((v) => Object.keys(v))
                .flat(),
        );
        if ((keys.has("label") && keys.has("value"), keys.size === 2)) {
            return r.map((c) => {
                return {
                    value: c.value.toString(),
                    label: {
                        __value: c.label.toString(),
                    },
                };
            });
        } else {
            return r.map((c) => rec(c));
        }
    } else {
        const funct = (k2: string) => [k2, rec(r[k2])] as const;

        const children = Object.keys(r).map(funct);

        const thiz = {
            ...Object.fromEntries(children),
        };
        return thiz;
    }
};
const mkLoc = async (
    marketCodeOverride?: string,
    businessCodeOverride?: string,
    languageCodeOverride?: string,
): Promise<Localization> => {
    const marketCode =
        marketCodeOverride || process.env.NEXT_PUBLIC_MARKET_CODE;
    const businessCode =
        businessCodeOverride || process.env.NEXT_PUBLIC_BUSINESS_CODE;
    const languageCode =
        languageCodeOverride ||
        process.env.NEXT_PUBLIC_LANGUAGE_CODE ||
        process.env.NEXT_PUBLIC_LANGUAGE?.substring(0, 2);

    const json = await merge(
        marketCode || "",
        businessCode || "",
        languageCode || "",
    );
    const x = Object.keys(json).map((k) => [k, rec(json[k])]);
    return {
        ...Object.fromEntries(x),
    } as Localization;
};

const annotated = (
    marketCodeOverride?: string,
    businessCodeOverride?: string,
    languageCodeOverride?: string,
) =>
    cache(async () => {
        const item = await mkLoc(
            marketCodeOverride,
            businessCodeOverride,
            languageCodeOverride,
        );
        return item;
    });

const withLMixedIn = (
    marketCodeOverride?: string,
    businessCodeOverride?: string,
    languageCodeOverride?: string,
) =>
    cache(async () => {
        const cached = structuredClone(
            await annotated(
                marketCodeOverride,
                businessCodeOverride,
                languageCodeOverride,
            )(),
        );
        mixInLToLeafs(cached);
        return cached;
    });

/**
 * This method should only be called to construct the namespace to be passed as a prop to a Client Component
 * This really should only ever be called by I18NContextProvider or similar
 * @param nameSpace key of the localization object
 * @param marketCodeOverride override for Testing.
 * @param businessCodeOverride override for Testing.
 * @param languageCodeOverride override for Testing.
 * @returns Just that key of the localization
 */
export const byNamespaceForClient = async <L extends keyof Localization>(
    nameSpace: L,
    marketCodeOverride?: string,
    businessCodeOverride?: string,
    languageCodeOverride?: string,
): Promise<Pick<Localization, L>> => {
    return Object.fromEntries([
        [
            nameSpace,
            (
                await annotated(
                    marketCodeOverride,
                    businessCodeOverride,
                    languageCodeOverride,
                )()
            )[nameSpace],
        ],
    ]) as Pick<Localization, L>;
};

/**
 * The result of this function can not be passed to a Client Component because it included functions which can not be serialized as Client Component Props.
 * @param marketCodeOverride override for Testing.
 * @param businessCodeOverride override for Testing.
 * @param languageCodeOverride override for Testing.
 * @returns fully functional `Localization`
 */
export const localizedForServerComponent = async (
    marketCodeOverride?: string,
    businessCodeOverride?: string,
    languageCodeOverride?: string,
): Promise<Localization> =>
    withLMixedIn(
        marketCodeOverride,
        businessCodeOverride,
        languageCodeOverride,
    )();

/**
The Below Monstrosity is because the method must be constructed in the client rather than in the server.
But we don't want to send down the whole `Localization` object if we don't have to.
We could instead place the methods in a common place and call it from there with these objects as inputs, but I dislike the ergonomics of that.
to wit:
```
  i18n.context.account.navigation.greeting.l({
    accountDisplayName: `${customerInfo.firstName} ${customerInfo.lastInitial}`,
        }),
```

reads much cleaner than

```
  i18n.l(i18n.context.account.navigation.greeting.value, {
    accountDisplayName: `${customerInfo.firstName} ${customerInfo.lastInitial}`,
        }),
```
It does however feel like type pain.

They can be called in either "client" or "server" code, but the resulting object can not pass the boundary.



 */
const mixInL = (current: LocalizationLeaf): void => {
    if (current.__value.includes("{{")) {
        (current as InterpString<{}>)["l"] = (t?: {}) =>
            t ? tmpl(current.__value, t) : current.__value; // Type Safety is assured from the autogenerated type declaration.
    } else {
        (current as NoIterpString)["l"] = () => current.__value;
    }
};

/**
 * This method mixes in the correct localization method INTO the input.
 * This Method is NOT PURE and treats `subgroup` as a mutable object.
 * The reason is because we are going to be impacting every row of the tree and copying it would be a lot of unused memory.
 *
 * @param subgroup
 */
const internalMixInLToLeafs = (
    subgroup: LocalizationSubGroup,
): LocalizationSubGroup => {
    Object.values(subgroup).forEach((element) => {
        if (typeof element === "object" && "__value" in element) {
            mixInL(element as LocalizationLeaf);
        } else {
            if (Array.isArray(element)) {
                const head = element.at(0);
                if (
                    head &&
                    "label" in head &&
                    "value" in head &&
                    Object.keys(head).length === 2
                ) {
                    // Element is an LocalizationValueLabelArrayInternal
                    element.forEach((e) => {
                        mixInL(e.label as LocalizationLeaf);
                    });
                } else {
                    // Element is an LocalizationSubGroupArray
                    element.forEach((e) => {
                        internalMixInLToLeafs(e);
                    });
                }
            } else if (typeof element === "object") {
                internalMixInLToLeafs(element as LocalizationSubGroup);
            }
        }
    });
    return subgroup;
};

/**
 * This method mixes in the correct localization method INTO the input.
 * This Method is NOT PURE and treats `subgroup` as a mutable object.
 * The reason is because we are going to be impacting every row of the tree and copying it would be a lot of unused memory.
 *
 * @param subgroup
 */
export const mixInLToLeafs = <T extends Partial<Localization>>(
    subgroup: T,
): T => {
    internalMixInLToLeafs(subgroup as LocalizationSubGroup);
    return subgroup;
};
